import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Building, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", address: "" };

export default function Branches() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(isSuper ? "" : user.tenant_id);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(!isSuper);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (tid) => {
    if (isSuper && !tid) { setBranches([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/branches", { params: isSuper ? { tenant_id: tid } : {} });
      setBranches(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isSuper]);

  useEffect(() => {
    if (isSuper) api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    else load();
  }, [isSuper, load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/branches/${editing.id}`, { name: form.name, address: form.address });
        toast.success("Branch updated");
      } else {
        await api.post("/branches", { ...form, ...(isSuper ? { tenant_id: tenantId } : {}) });
        toast.success("Branch created");
      }
      setOpen(false); setEditing(null); setForm(empty);
      load(tenantId);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    try { await api.delete(`/branches/${b.id}`); toast.success("Branch deleted"); load(tenantId); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, address: b.address || "" }); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="branches-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Branch offices for your organization — used on user profiles and printed vouchers.</p>
        <div className="flex items-center gap-3">
          {isSuper && (
            <Select value={tenantId} onValueChange={(v) => { setTenantId(v); load(v); }}>
              <SelectTrigger className="w-52 rounded-sm" data-testid="branch-tenant-filter"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" disabled={isSuper && !tenantId} onClick={openNew} data-testid="new-branch-btn"><Plus className="h-4 w-4 mr-2" />New Branch</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Branch" : "New Branch"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="space-y-2"><Label>Branch name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-name" className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city, postcode" data-testid="branch-address" className="rounded-sm" /></div>
                <DialogFooter>
                  <Button type="submit" disabled={busy} className="rounded-sm" data-testid="branch-submit">
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? "Save" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {isSuper && !tenantId ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Select a tenant to manage its branches.</div>
        ) : loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : branches.length === 0 ? (
          <div className="p-12 text-center">
            <Building className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No branches yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => (
                <TableRow key={b.id} className="hover:bg-muted/50" data-testid={`branch-row-${b.id}`}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{b.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`branch-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`branch-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-branch-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {b.name}?</AlertDialogTitle>
                            <AlertDialogDescription>Users assigned to this branch will have their branch cleared. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`branch-delete-cancel-${b.id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(b)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`branch-delete-confirm-${b.id}`}>Delete Branch</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
